import { __assign, __awaiter, __extends, __generator } from "tslib";
import { SdkMessageTypes } from '../constants';
import { DialogContentType, } from '../interfaces';
import { FrameContexts } from '../messages.interface';
import { isValidUrlForApp } from '../utils';
import { getMinimumAdaptiveCardSchemaVersionAsString, isHostAdaptiveCardSchemaVersionUnsupported, } from '../utils/helpers';
import { BaseMessageHandler } from './base-message-handler';
import { callServiceAndFormulateLogMessageResult, formulateAppMessageResult } from './messageHandlingUtils';
var dialogCancelledOrClosedMessage = 'User cancelled/closed the task module.';
/**
 * Responsible for common dialogs module related message requests.
 */
var DialogsMessageHandler = /** @class */ (function (_super) {
    __extends(DialogsMessageHandler, _super);
    function DialogsMessageHandler(appSdkHost, dialogService, telemetryService, hostVersionsInfo) {
        var _this = _super.call(this, appSdkHost, telemetryService) || this;
        _this.appSdkHost = appSdkHost;
        _this.dialogService = dialogService;
        _this.hostVersionsInfo = hostVersionsInfo;
        return _this;
    }
    DialogsMessageHandler.prototype.initHandlers = function () {
        var _this = this;
        if (this.appSdkHost.getFrameContext() === FrameContexts.task) {
            if (!this.dialogService) {
                throw new Error('DialogService needs to be provided for FrameContexts.task');
            }
        }
        this.registerHandler(SdkMessageTypes.dialogs.submit, function (_, result, appIds) {
            return callServiceAndFormulateLogMessageResult(_this.dialogService.urlService.submit, result, appIds);
        });
        if (this.appSdkHost.getFrameContext() === FrameContexts.task) {
            this.dialogService.urlService.addEventListener('onMessageForChild', function (event) {
                var _a;
                var scenario = (_a = _this.telemetryService) === null || _a === void 0 ? void 0 : _a.createScenario('eventDispatch.onMessageForChild');
                scenario === null || scenario === void 0 ? void 0 : scenario.stop();
                if (!Array.isArray(event.data))
                    event.data = [event.data];
                _this.appSdkHost.sendRequestMessage('messageForChild', event.data ? event.data : []);
            });
        }
        this.registerHandler(SdkMessageTypes.dialogs.open, function (_, dialogInfo) {
            if (dialogInfo.card && dialogInfo.url) {
                return formulateAppMessageResult([
                    'Values for both card and url were specified. One or the other, but not both, are allowed.',
                ]);
            }
            var dialog;
            if (dialogInfo.card) {
                dialog = __assign(__assign({}, dialogInfo), { card: dialogInfo.card, type: DialogContentType.Card });
                if ('completionBotId' in dialog) {
                    return _this.callDialogWithBotAndAdaptiveCard(dialog);
                }
                else {
                    return _this.callDialogWithCardService(dialog);
                }
            }
            else if (dialogInfo.url) {
                var context = _this.appSdkHost.getContext();
                if (!isValidUrlForApp(dialogInfo.url, _this.appSdkHost.getLogger(), _this.appDefinition, context)) {
                    return formulateAppMessageResult(['Invalid dialog URL']);
                }
                dialog = __assign(__assign({}, dialogInfo), { url: dialogInfo.url, type: DialogContentType.Url });
                if ('completionBotId' in dialog) {
                    return _this.callDialogWithUrlAndBotService(dialog);
                }
                else {
                    return _this.callDialogWithJustUrlService(dialog);
                }
            }
            else {
                return formulateAppMessageResult(['You must specify a value for either card or url.']);
            }
        });
        this.registerHandler('messageForParent', function (_, message) {
            return callServiceAndFormulateLogMessageResult(_this.dialogService.urlService.messageForParent, message);
        });
        this.registerHandler('messageForChild', function (_, message) {
            return callServiceAndFormulateLogMessageResult(_this.dialogService.urlService.messageForChild, message);
        });
    };
    DialogsMessageHandler.prototype.validateDependencies = function () {
        if (!this.dialogService) {
            throw 'No dialogService provided';
        }
        if (this.dialogService.cardService &&
            (!this.hostVersionsInfo ||
                !this.hostVersionsInfo.adaptiveCardSchemaVersion ||
                isHostAdaptiveCardSchemaVersionUnsupported(this.hostVersionsInfo.adaptiveCardSchemaVersion))) {
            throw "dialogService.cardService requires that the host specifies an adaptive card version of at least ".concat(getMinimumAdaptiveCardSchemaVersionAsString());
        }
    };
    DialogsMessageHandler.prototype.callDialogWithBotAndAdaptiveCard = function (dialog) {
        return __awaiter(this, void 0, void 0, function () {
            var result, _1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 4, , 5]);
                        if (!this.dialogService.cardService.cardBotService) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.dialogService.cardService.cardBotService.open(dialog)];
                    case 1:
                        result = _a.sent();
                        return [2 /*return*/, formulateAppMessageResult([null, result])];
                    case 2: return [2 /*return*/, formulateAppMessageResult(['Bot Adaptive Card based Dialogs are not supported'])];
                    case 3: return [3 /*break*/, 5];
                    case 4:
                        _1 = _a.sent();
                        return [2 /*return*/, formulateAppMessageResult([dialogCancelledOrClosedMessage])];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    DialogsMessageHandler.prototype.callDialogWithCardService = function (dialog) {
        return __awaiter(this, void 0, void 0, function () {
            var result, _2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 4, , 5]);
                        if (!this.dialogService.cardService) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.dialogService.cardService.open(dialog)];
                    case 1:
                        result = _a.sent();
                        return [2 /*return*/, formulateAppMessageResult([null, result])];
                    case 2: return [2 /*return*/, formulateAppMessageResult(['Adaptive Card based Dialogs are not supported'])];
                    case 3: return [3 /*break*/, 5];
                    case 4:
                        _2 = _a.sent();
                        return [2 /*return*/, formulateAppMessageResult([dialogCancelledOrClosedMessage])];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    DialogsMessageHandler.prototype.callDialogWithUrlAndBotService = function (dialog) {
        if (this.dialogService.urlService.urlBotService) {
            return this.addRemoveEventListenerAndCreateSDKResponse(this.dialogService.urlService.urlBotService.open, dialog);
        }
        else {
            return formulateAppMessageResult(['Dialogs with completionBotId are not supported']);
        }
    };
    DialogsMessageHandler.prototype.callDialogWithJustUrlService = function (dialog) {
        return this.addRemoveEventListenerAndCreateSDKResponse(this.dialogService.urlService.open, dialog);
    };
    DialogsMessageHandler.prototype.addRemoveEventListenerAndCreateSDKResponse = function (serviceCall, params) {
        return __awaiter(this, void 0, void 0, function () {
            var result, _3;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, 3, 4]);
                        this.dialogService.urlService.clearListeners();
                        this.dialogService.urlService.addEventListener('onMessageForParent', function (event) {
                            var _a;
                            var scenario = (_a = _this.telemetryService) === null || _a === void 0 ? void 0 : _a.createScenario('eventDispatch.onMessageForParent');
                            scenario === null || scenario === void 0 ? void 0 : scenario.stop();
                            if (!Array.isArray(event.data))
                                event.data = [event.data];
                            _this.appSdkHost.sendRequestMessage('messageForParent', event.data ? event.data : []);
                        });
                        return [4 /*yield*/, serviceCall(params)];
                    case 1:
                        result = _a.sent();
                        return [2 /*return*/, formulateAppMessageResult([null, result])];
                    case 2:
                        _3 = _a.sent();
                        return [2 /*return*/, formulateAppMessageResult([dialogCancelledOrClosedMessage])];
                    case 3:
                        this.dialogService.urlService.clearListeners();
                        return [7 /*endfinally*/];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    return DialogsMessageHandler;
}(BaseMessageHandler));
export { DialogsMessageHandler };
//# sourceMappingURL=dialogs-message-handler.js.map